import { GetLocationHolidays } from "@/server-types";
import { cn } from "@/utils";
import { Button, Calendar, Popover } from "@heroui-v3/react";
import {
  CalendarDate,
  type DateValue,
  getLocalTimeZone,
  toCalendarDate,
  today,
} from "@internationalized/date";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

type LocationHoliday = GetLocationHolidays["response"]["holidays"][number];
/** `fiveWeeks` is five weeks from the selected date's Monday. */
type CalendarView = "day" | "week" | "fiveWeeks" | "month";

function monthKey(date: DateValue) {
  return `${date.year}-${String(date.month).padStart(2, "0")}`;
}

function viewDuration(view: CalendarView, sign: 1 | -1) {
  if (view === "day") return { days: sign };
  if (view === "week") return { weeks: sign };
  if (view === "fiveWeeks") return { weeks: 5 * sign };
  return { months: sign };
}

export const LimeDateTimeframePicker = ({
  selectedDate,
  onDateChange,
  minDate,
  maxDate,
  calendarView,
  isOpen,
  onOpenChange,
  holidays,
  onVisibleMonthChange,
}: {
  selectedDate: CalendarDate | null;
  onDateChange: (date: CalendarDate | null) => void;
  minDate?: CalendarDate | null;
  maxDate?: CalendarDate | null;
  calendarView: CalendarView;
  /**
   * Optional controlled open state. When omitted the popover manages its own
   * open state (uncontrolled). Pass both to let a parent force it closed — e.g.
   * the calendar closes it when an appointment drawer opens, since HeroUI's
   * outside-press dismissal can't reach it past the appointment hover tooltip.
   */
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Public holidays to mark in the calendar grid (blue dot + name tooltip).
   * Only days within the currently displayed month are marked. Parents should
   * fetch holidays for the month surfaced via {@link onVisibleMonthChange}.
   */
  holidays?: LocationHoliday[];
  /**
   * Called when the user navigates the popover to a different month, so the
   * parent can fetch holidays for the newly visible month. Receives the first
   * day of that month.
   */
  onVisibleMonthChange?: (firstDayOfMonth: Date) => void;
}) => {
  const { i18n } = useLingui();
  const todayDate = useMemo(() => today(getLocalTimeZone()), []);
  const selectionOrToday = selectedDate ?? todayDate;
  const selectedMonthKey = monthKey(selectionOrToday);

  const [focusedDate, setFocusedDate] = useState(selectionOrToday);
  const [prevSelectedMonthKey, setPrevSelectedMonthKey] =
    useState(selectedMonthKey);
  if (selectedMonthKey !== prevSelectedMonthKey) {
    setPrevSelectedMonthKey(selectedMonthKey);
    setFocusedDate(selectionOrToday);
  }

  const holidayNameByDate = useMemo(() => {
    const map = new Map<string, string>();
    holidays?.forEach((holiday) => map.set(holiday.date, holiday.name));
    return map;
  }, [holidays]);

  const triggerText = useMemo(() => {
    if (!selectedDate) return "";

    if (calendarView === "day") {
      return selectedDate
        .toDate(getLocalTimeZone())
        .toLocaleDateString(i18n.locale);
    } else if (calendarView === "week") {
      const start = dayjs(selectedDate.toDate(getLocalTimeZone()))
        .locale("sl") // Use Slovenian locale for the week start
        .weekday(0);
      const end = dayjs(selectedDate.toDate(getLocalTimeZone()))
        .locale("sl") // Use Slovenian locale for the week end
        .weekday(6);

      return `${start.toDate().toLocaleDateString(i18n.locale)} - ${end.toDate().toLocaleDateString(i18n.locale)}`;
    } else if (calendarView === "fiveWeeks") {
      const start = dayjs(selectedDate.toDate(getLocalTimeZone()))
        .locale("sl")
        .weekday(0);
      const end = start.add(34, "day");

      return `${start.toDate().toLocaleDateString(i18n.locale)} - ${end.toDate().toLocaleDateString(i18n.locale)}`;
    } else if (calendarView === "month") {
      return dayjs(selectedDate.toDate(getLocalTimeZone()))
        .locale(i18n.locale)
        .format("MMMM YYYY");
    }
  }, [selectedDate, calendarView, i18n.locale]);

  const canGoBack = useMemo(() => {
    if (!selectedDate || !minDate) return true;
    return (
      selectedDate.subtract(viewDuration(calendarView, 1)).compare(minDate) >= 0
    );
  }, [selectedDate, minDate, calendarView]);

  const canGoForward = useMemo(() => {
    if (!selectedDate || !maxDate) return true;
    return (
      selectedDate.add(viewDuration(calendarView, 1)).compare(maxDate) <= 0
    );
  }, [selectedDate, maxDate, calendarView]);

  function handleDirectionClick(direction: "prev" | "next") {
    if (!selectedDate || !onDateChange) {
      return;
    }

    const nextDate =
      direction === "prev"
        ? selectedDate.subtract(viewDuration(calendarView, 1))
        : selectedDate.add(viewDuration(calendarView, 1));

    onDateChange(nextDate);
  }

  function handleFocusChange(date: DateValue) {
    const previousMonthKey = monthKey(focusedDate);
    const nextMonthKey = monthKey(date);
    setFocusedDate(toCalendarDate(date));
    if (nextMonthKey !== previousMonthKey) {
      onVisibleMonthChange?.(date.set({ day: 1 }).toDate(getLocalTimeZone()));
    }
  }

  return (
    <div className="flex items-center" data-identifier="date-picker-outer">
      <Button
        isIconOnly
        variant="ghost"
        size="sm"
        onPress={() => handleDirectionClick("prev")}
        isDisabled={!canGoBack}
        aria-label={t`Prejšnja perioda`}
        data-identifier="prev-day-button"
      >
        <ChevronLeft size={16} />
      </Button>

      <Popover isOpen={isOpen} onOpenChange={onOpenChange}>
        <Button
          variant="ghost"
          size="sm"
          data-view={calendarView}
          className={cn({
            "min-w-25": calendarView === "day",
            "min-w-46.25":
              calendarView === "week" || calendarView === "fiveWeeks",
            "min-w-30": calendarView === "month",
          })}
          aria-label={triggerText || t`Izberi datum`}
          data-identifier="date-picker-trigger"
          data-date={selectedDate?.toDate(getLocalTimeZone()).toISOString()}
        >
          {triggerText}
        </Button>
        <Popover.Content className="p-0">
          <Popover.Dialog className="p-3">
            <Calendar
              aria-label={t`Izberi datum`}
              value={selectedDate}
              onChange={onDateChange}
              minValue={minDate ?? undefined}
              maxValue={maxDate ?? undefined}
              focusedValue={focusedDate}
              onFocusChange={handleFocusChange}
            >
              <Calendar.Header>
                <Calendar.YearPickerTrigger>
                  <Calendar.YearPickerTriggerHeading />
                  <Calendar.YearPickerTriggerIndicator />
                </Calendar.YearPickerTrigger>
                <Calendar.NavButton slot="previous" />
                <Calendar.NavButton slot="next" />
              </Calendar.Header>
              <Calendar.Grid>
                <Calendar.GridHeader>
                  {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
                </Calendar.GridHeader>
                <Calendar.GridBody>
                  {(date) => (
                    <Calendar.Cell date={date}>
                      {({ formattedDate, isOutsideMonth }) => {
                        const holidayName = isOutsideMonth
                          ? undefined
                          : holidayNameByDate.get(date.toString());

                        return (
                          <span
                            title={holidayName}
                            className="flex size-full items-center justify-center"
                          >
                            {formattedDate}
                            {holidayName ? (
                              <Calendar.CellIndicator
                                data-holiday=""
                                aria-hidden={false}
                                role="img"
                                aria-label={holidayName}
                              />
                            ) : null}
                          </span>
                        );
                      }}
                    </Calendar.Cell>
                  )}
                </Calendar.GridBody>
              </Calendar.Grid>
              <Calendar.YearPickerGrid>
                <Calendar.YearPickerGridBody>
                  {({ year }) => <Calendar.YearPickerCell year={year} />}
                </Calendar.YearPickerGridBody>
              </Calendar.YearPickerGrid>
            </Calendar>
          </Popover.Dialog>
        </Popover.Content>
      </Popover>

      <Button
        isIconOnly
        variant="ghost"
        size="sm"
        onPress={() => handleDirectionClick("next")}
        isDisabled={!canGoForward}
        aria-label={t`Naslednja perioda`}
        data-identifier="next-day-button"
      >
        <ChevronRight size={16} />
      </Button>
    </div>
  );
};
